// Seat completeness (CHART-KB, docs/design/2026-10-21-chart-kb.md): the founder's "profile
// completion %" — "if a seat does not have financials or the keystones it is not considered
// complete". Not a score of the work (the lenses are) but of the SETUP: a seat is complete when
// every item the company requires is in place. The items are the chart's own dimensions, one
// per source of truth:
//   - `holder`: someone holds the seat, or it is an open seat with its hiring status set
//     (`seat.status = open`; `proposed` is not a decision yet);
//   - `reports_to`: a supervisor (`supervisor_seat_id`); the top of the chart — a seat with
//     reports and nobody above it — is exempt, a stray seat with neither is not;
//   - `team`: a team membership;
//   - `responsibility`: at least one active responsibility;
//   - `measurable`: at least one scorecard metric on the seat with a target (seat_metric with
//     a target rule); only counted while the company has the `kpi` add-on;
//   - `finance`: a budget on the seat or a cost line this month; a seat held by a digital
//     employee is exempt when `de_exempt_finance` (its cost comes from its subscription);
//   - `keystone`: at least one priority (`goal.kind = priority`) for the current quarter.
// An admin marks which items are required (Setup › Chart); the score is met ÷ required. The
// API computes it on the server from the whole seat (never cut to the caller) and emits it to
// the seat's holders, its reporting line and managers; a missing item in a gated dimension is
// named only to that dimension's audience and counted as `hidden` for the rest, so the
// percentage is the same for everyone who may see it and no item leaks through its absence.
export type CompletenessItem =
  | "holder"
  | "reports_to"
  | "team"
  | "responsibility"
  | "measurable"
  | "finance"
  | "keystone";

/** The items in the order the panel lists them. */
export const COMPLETENESS_ITEMS: readonly CompletenessItem[] = [
  "holder",
  "reports_to",
  "team",
  "responsibility",
  "measurable",
  "finance",
  "keystone",
];

export type CompletenessRules = {
  required: Record<CompletenessItem, boolean>;
  /** A digital employee's seat needs no budget or cost line. */
  de_exempt_finance: boolean;
};

/** Every item required, DE seats exempt from finance (the founder's rule). */
export const DEFAULT_COMPLETENESS_RULES: CompletenessRules = {
  required: {
    holder: true,
    reports_to: true,
    team: true,
    responsibility: true,
    measurable: true,
    finance: true,
    keystone: true,
  },
  de_exempt_finance: true,
};

/** The company's rules over the defaults (a missing key is required). */
export function completenessRulesOf(
  set:
    | {
        required?: { [K in CompletenessItem]?: boolean | undefined } | undefined;
        de_exempt_finance?: boolean | undefined;
      }
    | null
    | undefined,
): CompletenessRules {
  const required = { ...DEFAULT_COMPLETENESS_RULES.required };
  for (const item of COMPLETENESS_ITEMS) {
    const v = set?.required?.[item];
    if (v !== undefined) required[item] = v;
  }
  return {
    required,
    de_exempt_finance: set?.de_exempt_finance ?? DEFAULT_COMPLETENESS_RULES.de_exempt_finance,
  };
}

/** What a seat has, as the API reads it (every count over the whole seat, never cut). */
export type SeatFacts = {
  held: boolean;
  status: "proposed" | "open" | "filled" | "paused" | "closed";
  /** A digital employee holds it (alone or with a person). */
  digital: boolean;
  supervisor: boolean;
  /** The seat has direct reports (with no supervisor it is the top of the chart). */
  reports: boolean;
  teams: number;
  responsibilities: number;
  /** Scorecard metrics on the seat that have a target; null while the company has no `kpi`. */
  measurables: number | null;
  /** A budget on the seat, or a cost line this month. */
  finance: boolean;
  /** Priorities of the current quarter. */
  keystones: number;
};

export type Completeness = {
  /** met ÷ required in %, 100 when nothing is required. */
  score: number;
  met: number;
  required: number;
  missing: CompletenessItem[];
};

/** Whether an item applies to the seat at all under the rules (a required item that does not apply is not counted). */
export function itemApplies(
  item: CompletenessItem,
  f: SeatFacts,
  rules: CompletenessRules,
): boolean {
  if (!rules.required[item]) return false;
  switch (item) {
    case "reports_to":
      // The top of the chart reports to nobody by design.
      return !(f.reports && !f.supervisor);
    case "measurable":
      return f.measurables !== null;
    case "finance":
      return !(rules.de_exempt_finance && f.digital);
    default:
      return true;
  }
}

/** Whether the seat has the item. */
export function itemMet(item: CompletenessItem, f: SeatFacts): boolean {
  switch (item) {
    case "holder":
      return f.held || f.status === "open";
    case "reports_to":
      return f.supervisor;
    case "team":
      return f.teams > 0;
    case "responsibility":
      return f.responsibilities > 0;
    case "measurable":
      return (f.measurables ?? 0) > 0;
    case "finance":
      return f.finance;
    case "keystone":
      return f.keystones > 0;
  }
}

/** The seat's completeness under the rules: the applicable items, how many are met, the missing ones in list order. */
export function seatCompleteness(f: SeatFacts, rules: CompletenessRules): Completeness {
  const applicable = COMPLETENESS_ITEMS.filter((i) => itemApplies(i, f, rules));
  const missing = applicable.filter((i) => !itemMet(i, f));
  const required = applicable.length;
  const met = required - missing.length;
  return {
    score: required === 0 ? 100 : Math.round((met / required) * 100),
    met,
    required,
    missing,
  };
}

/** "78 % of the seats' setup is in place": the mean score over the seats and how many are complete. */
export function completenessSummary(scores: readonly number[]): {
  seats: number;
  complete: number;
  mean: number;
} {
  const seats = scores.length;
  if (seats === 0) return { seats: 0, complete: 0, mean: 100 };
  return {
    seats,
    complete: scores.filter((s) => s >= 100).length,
    mean: Math.round(scores.reduce((a, b) => a + b, 0) / seats),
  };
}

/** The gated dimension an item belongs to (its missing state follows that audience); null for the floor. */
export const ITEM_GATE: Partial<Record<CompletenessItem, "kpi" | "cost" | "keystones">> = {
  measurable: "kpi",
  finance: "cost",
  keystone: "keystones",
};

/**
 * The completeness as one caller may read it: the missing items in dimensions the caller
 * reaches are named, the others counted as `hidden`.
 */
export function cutCompleteness(
  c: Completeness,
  reaches: (gate: "kpi" | "cost" | "keystones") => boolean,
): Completeness & { hidden: number } {
  const missing = c.missing.filter((i) => {
    const gate = ITEM_GATE[i];
    return !gate || reaches(gate);
  });
  return { ...c, missing, hidden: c.missing.length - missing.length };
}

/** The completeness band: complete is green, four in five amber, below red (the lens's own steps). */
export function completenessBand(score: number): "green" | "amber" | "red" {
  if (score >= 100) return "green";
  if (score >= 80) return "amber";
  return "red";
}
